/**
 * Follows a running PDF import around the app.
 *
 * Start an import and walk away and the parse keeps going — it runs on the job
 * queue, so it survives the page, the tab, and the browser. Until now nothing
 * told you that: the progress lived on the import screen, and leaving it meant
 * finding out by going back and looking.
 *
 * Mounted in the authenticated Layout, so it is present on every page. It polls
 * the one remembered session and keeps a toast pinned at the bottom of the
 * screen: which phase, how far through the photos, and — when it lands — a
 * button to go and review it.
 *
 * ── Two things it deliberately does NOT do ─────────────────────────────────
 * It stays quiet while you are on the import screen itself: that page already
 * shows the same progress in more detail, and a toast repeating it is noise.
 *
 * It does not hold any of the parse's state. The session id is the only thing
 * remembered locally (see active-import.ts); stage, progress and result all
 * come from the server, so a refresh, a second tab, or coming back an hour
 * later all see the same truth rather than a stale copy.
 */
import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { api } from "@/lib/api-client";
import { useAuth } from "@/contexts/AuthContext";
import { roleBase } from "@/routes/role-paths";
import { forgetActiveImport, readActiveImport } from "./active-import";
import { decideWatch } from "./watch-decision";

/** Same shape the import screen polls — one endpoint, one truth. */
interface SessionResponse {
  sessionId: number;
  status: "parsing" | "ready" | "failed";
  stage: "extracting" | "parsing" | "matching" | "uploading_images" | null;
  progress: { done: number; total: number };
  filename: string | null;
  error: string | null;
}

const PHASE_LABEL: Record<NonNullable<SessionResponse["stage"]>, string> = {
  extracting:       "Reading the PDF",
  parsing:          "Finding questions and answers",
  matching:         "Matching against the checklist",
  uploading_images: "Saving the report's photos",
};

/** Sonner id, so each update replaces the toast instead of stacking a new one. */
const TOAST_ID = "sire-import-progress";
const POLL_MS = 2000;

export function ImportWatcher() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  // Read once per render rather than holding it in state: the value only
  // changes through this component's own effects and the import screen, and
  // re-reading keeps the two from drifting.
  const sessionId = readActiveImport();

  const onImportScreen = location.pathname.endsWith("/manage-inspections/import-report");

  const sessionQ = useQuery({
    queryKey: ["sire-import-session", sessionId],
    queryFn: () => api.one<SessionResponse>(`/api/sire-import/sessions/${sessionId}`),
    enabled: sessionId != null,
    refetchInterval: (q) => {
      const s = q.state.data?.status;
      return s === "ready" || s === "failed" ? false : POLL_MS;
    },
    // A missing session is the normal end of the story (swept, or the company
    // it belonged to went away), not something to keep retrying.
    retry: false,
  });

  /* Which import the failure toast was shown for — keyed by session, so a
   * second import is never mistaken for the first. See watch-decision.ts. */
  const failureShownFor = useRef<number | null>(null);

  /* One effect because it is one decision — made by decideWatch, which is
   * where the rules live (and are tested); this only acts on it. */
  useEffect(() => {
    const data = sessionQ.data;
    const decision = decideWatch({
      sessionId,
      gone: sessionQ.isError,
      status: data?.status ?? null,
      onImportScreen,
      failureShownFor: failureShownFor.current,
    });
    if (decision.forget) forgetActiveImport();

    if (decision.toast === "dismiss") {
      toast.dismiss(TOAST_ID);
      return;
    }
    if (!data || decision.toast === "none") return;

    if (decision.toast === "ready") {
      /* Deliberately does NOT say "imported". Nothing has been written yet —
       * the parse only produced a preview, and the inspection is created when
       * the user saves. Saying "parsed" and leaving it there invites people to
       * walk away believing they are done. So the toast names the two things
       * still required (the Save button is gated on inspectors and the date)
       * and the button says finish, not review.
       *
       * Shown again each time the user leaves the import screen without
       * finishing — the same toast id, so it replaces rather than stacks. */
      toast.success("Report parsed — not imported yet", {
        id: TOAST_ID,
        position: "bottom-right",
        duration: Infinity,
        description: data.filename
          ? `${data.filename} is ready. Choose the inspector(s) who carried it out, check the inspection date, then save to create the inspection.`
          : "Your report is ready. Choose the inspector(s) who carried it out, check the inspection date, then save to create the inspection.",
        action: {
          label: "Finish import",
          onClick: () => navigate(`${roleBase(user?.role)}/manage-inspections/import-report`),
        },
        cancel: {
          label: "Discard",
          onClick: () => forgetActiveImport(),
        },
      });
      return;
    }

    if (decision.toast === "failed") {
      failureShownFor.current = sessionId;
      toast.error("Couldn't parse the report", {
        id: TOAST_ID,
        position: "bottom-right",
        duration: Infinity,
        description: data.error ?? "The report could not be parsed.",
      });
      return;
    }
    const phase = data.stage ? PHASE_LABEL[data.stage] : "Starting";
    const count =
      data.stage === "uploading_images" && data.progress.total > 0
        ? ` — ${data.progress.done} of ${data.progress.total}`
        : "";
    toast.loading("Parsing your report", {
      id: TOAST_ID,
      position: "bottom-right",
      duration: Infinity,
      // Sets the expectation early: this finishes with a step, not on its own.
      description: `${phase}${count}. Keep working — you'll be asked to pick the inspector and save when it's ready.`,
    });
  }, [sessionId, sessionQ.data, sessionQ.isError, onImportScreen, navigate, user?.role]);

  // Clear the toast if this unmounts (sign-out, or the app tearing down), so a
  // pinned toast can't outlive the session that justified it.
  useEffect(() => () => {
    toast.dismiss(TOAST_ID);
  }, []);

  return null;
}
